import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type GeneralInputOption = {
  code?: string;
  id?: string;
  is_visible?: boolean;
  label?: string;
  priority?: number;
};

type GetGeneralInputOptionsFn = (
  id: string,
  params?: {
    keyword: string;
    page: number;
    per: number;
  }
) => Promise<{
  data?: {
    ap_general_input_options?: GeneralInputOption[];
  };
}>;

type GetGeneralInputOptionFn = (
  propertyId: string,
  id: string
) => Promise<{
  data?: GeneralInputOption;
}>;

type UsePulldownOptionsParams = {
  propertyId: string;
  per?: number;
  getGeneralInputOptions: GetGeneralInputOptionsFn;
};

type UsePulldownOptionParams = {
  id: string | undefined;
  propertyId: string;
  hasOption?: boolean;
  getGeneralInputOption: GetGeneralInputOptionFn;
  optionsCache?: Map<string, GeneralInputOption[]>;
};

export const usePulldownOptions = ({
  propertyId,
  per = 25,
  getGeneralInputOptions,
}: UsePulldownOptionsParams) => {
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [query, setQuery] = useState<string>('');

  const {
    data: apiData,
    error,
    isLoading,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: [
      'ap_invoice_transaction_general_input_options',
      propertyId,
      query,
    ],
    queryFn: async ({ pageParam }) => {
      const response = await getGeneralInputOptions(propertyId, {
        keyword: query,
        page: pageParam,
        per: per,
      });
      return response.data?.ap_general_input_options ?? [];
    },
    getNextPageParam: (lastPage, _, lastPageParam) => {
      if (lastPage && lastPage.length === per) {
        return lastPageParam + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    enabled: menuIsOpen,
  });

  const fetchMore = useCallback(
    async (callback: () => void) => {
      if (hasNextPage) {
        await fetchNextPage();
      }
      callback();
    },
    [fetchNextPage, hasNextPage]
  );

  const onSearch = useCallback((value: string) => {
    startTransition(() => {
      setQuery(value);
    });
  }, []);

  const handleDropdownVisibleChange = useCallback((open: boolean) => {
    if (open) {
      setMenuIsOpen(true);
    } else {
      setMenuIsOpen(false);
    }
  }, []);

  return {
    isLoading,
    error,
    onSearch,
    fetchMore,
    handleDropdownVisibleChange,
    optionList: apiData?.pages.flat() ?? [],
    query,
    menuIsOpen,
  };
};

export const usePulldownOption = ({
  id,
  propertyId,
  hasOption,
  getGeneralInputOption,
  optionsCache,
}: UsePulldownOptionParams) => {
  const [initialLoad, setInitialLoad] = useState(true);
  const cachedOptions = optionsCache?.get(propertyId);

  const cachedOption = useMemo(() => {
    if (!cachedOptions || !id) {
      return undefined;
    }
    return cachedOptions.find((item) => item.id === id);
  }, [cachedOptions, id]);

  const {
    data: optionData,
    error,
    isLoading,
  } = useQuery({
    queryKey: ['ap_invoice_transaction_general_input_options', propertyId, id],
    queryFn: async () => {
      const optionData = await getGeneralInputOption(propertyId, id ?? '');
      return optionData.data;
    },
    enabled: id !== undefined && initialLoad && !cachedOption && !hasOption,
  });

  const defaultOption = useMemo(() => {
    if (cachedOption) {
      return [
        {
          value: cachedOption.id?.toString() ?? '',
          label: (cachedOption.label ?? '').concat(
            cachedOption.code ? `（${cachedOption.code}）` : ''
          ),
          caption: cachedOption.code ?? '',
        },
      ];
    }

    if (!optionData?.id) {
      return [];
    }

    return [
      {
        value: optionData?.id?.toString() ?? '',
        label: (optionData?.label ?? '').concat(
          optionData?.code ? `（${optionData?.code}）` : ''
        ),
        caption: optionData?.code ?? '',
      },
    ];
  }, [optionData, cachedOption]);

  useEffect(() => {
    if (initialLoad && (optionData || cachedOption || hasOption)) {
      setInitialLoad(false);
    }
  }, [initialLoad, optionData, cachedOption, hasOption]);

  return {
    defaultOption,
    isLoading,
    error,
  };
};
